import { useState, useEffect, useCallback } from 'react';
import SALayout from './SALayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  LifeBuoy, Loader2, Send, Flag, Tag, ChevronLeft, MessageSquare,
  Building2, User, Clock, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
];

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
};

const PRIORITY_COLORS = { low: 'text-slate-400', medium: 'text-amber-500', high: 'text-red-500' };

export default function SASupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const token = localStorage.getItem('sa_token');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const r = await fetch(`${API_URL}/api/superadmin/support/tickets${params}`, { headers });
      const data = await r.json();
      setTickets(data);
    } catch { toast.error('Failed to load tickets'); }
    setLoading(false);
  }, [statusFilter, token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const updateTicket = async (ticketId, updates) => {
    try {
      const r = await fetch(`${API_URL}/api/superadmin/support/tickets/${ticketId}`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      const data = await r.json();
      setSelectedTicket(data);
      setTickets(prev => prev.map(t => t.id === ticketId ? data : t));
      toast.success('Ticket updated');
    } catch { toast.error('Failed to update ticket'); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setReplySubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/superadmin/support/tickets/${selectedTicket.id}/reply`, {
        method: 'POST', headers, body: JSON.stringify({ message: replyText })
      });
      const data = await r.json();
      setSelectedTicket(data);
      setTickets(prev => prev.map(t => t.id === data.id ? data : t));
      setReplyText('');
    } catch { toast.error('Failed to send reply'); }
    setReplySubmitting(false);
  };

  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    total: tickets.length
  };

  return (
    <SALayout>
      <div className="space-y-4" data-testid="sa-support-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Support Tickets</h1>
            <p className="text-sm text-slate-500">Manage support tickets across all organizations</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-slate-700' },
            { label: 'Open', value: stats.open, color: 'text-blue-600' },
            { label: 'In Progress', value: stats.in_progress, color: 'text-amber-600' },
            { label: 'Resolved', value: stats.resolved, color: 'text-emerald-600' }
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          {STATUS_OPTIONS.map(s => (
            <button key={s.value} onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${statusFilter === s.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`filter-${s.value}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex gap-4" style={{ height: 'calc(100vh - 320px)' }}>
          {/* Ticket List */}
          <div className={`${selectedTicket ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 flex-shrink-0 bg-white rounded-xl border border-slate-200 overflow-hidden`}>
            {loading ? (
              <div className="flex items-center justify-center flex-1">
                <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex items-center justify-center flex-1 text-center p-6">
                <div>
                  <LifeBuoy className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No tickets found</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {tickets.map(ticket => (
                  <button key={ticket.id} onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedTicket?.id === ticket.id ? 'bg-violet-50 border-l-2 border-l-violet-500' : ''}`}
                    data-testid={`sa-ticket-${ticket.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-900 truncate">{ticket.subject}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Building2 className="w-3 h-3 text-slate-400" />
                          <span className="text-[11px] text-slate-500 truncate">{ticket.organization_name}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">{ticket.id} &middot; {new Date(ticket.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[ticket.status]}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                        <Flag className={`w-3 h-3 ${PRIORITY_COLORS[ticket.priority]}`} />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ticket Detail */}
          <div className="flex-1 min-w-0">
            {selectedTicket ? (
              <div className="bg-white rounded-xl border border-slate-200 h-full flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedTicket(null)} className="md:hidden p-1 rounded hover:bg-slate-100">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs font-mono text-slate-400">{selectedTicket.id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={selectedTicket.status}
                        onChange={e => updateTicket(selectedTicket.id, { status: e.target.value })}
                        className="h-7 rounded-md border border-slate-200 text-xs px-2 bg-white"
                        data-testid="sa-ticket-status-select">
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      <select value={selectedTicket.priority}
                        onChange={e => updateTicket(selectedTicket.id, { priority: e.target.value })}
                        className="h-7 rounded-md border border-slate-200 text-xs px-2 bg-white"
                        data-testid="sa-ticket-priority-select">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-400">
                    <span><Building2 className="w-3 h-3 inline mr-0.5" />{selectedTicket.organization_name}</span>
                    <span><User className="w-3 h-3 inline mr-0.5" />{selectedTicket.created_by_name} ({selectedTicket.created_by_email})</span>
                    <span><Tag className="w-3 h-3 inline mr-0.5" />{selectedTicket.category.replace('_', ' ')}</span>
                    <span><Clock className="w-3 h-3 inline mr-0.5" />{new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {/* Original */}
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-slate-100">
                      <div className="flex items-center gap-1 mb-1">
                        <User className="w-3 h-3 text-blue-500" />
                        <span className="text-[11px] font-medium text-blue-600">{selectedTicket.created_by_name}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-slate-900">{selectedTicket.description}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedTicket.replies?.map(reply => (
                    <div key={reply.id} className={`flex ${reply.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        reply.sender_type === 'admin'
                          ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white'
                          : 'bg-slate-100 text-slate-900'
                      }`}>
                        <div className="flex items-center gap-1 mb-1">
                          {reply.sender_type === 'admin' ? <LifeBuoy className="w-3 h-3" /> : <User className="w-3 h-3 text-blue-500" />}
                          <span className={`text-[11px] font-medium ${reply.sender_type === 'admin' ? 'text-violet-200' : 'text-blue-600'}`}>
                            {reply.sender_type === 'admin' ? 'You (Support)' : reply.sender_name}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                        <p className={`text-[10px] mt-1 ${reply.sender_type === 'admin' ? 'text-violet-200' : 'text-slate-400'}`}>
                          {new Date(reply.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply */}
                <div className="p-3 border-t border-slate-100">
                  <form onSubmit={e => { e.preventDefault(); sendReply(); }} className="flex gap-2">
                    <Input value={replyText} onChange={e => setReplyText(e.target.value)}
                      placeholder="Reply to this ticket..." className="h-9 rounded-xl text-sm" data-testid="sa-ticket-reply-input" />
                    <Button type="submit" disabled={!replyText.trim() || replySubmitting}
                      className="h-9 px-3 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="sa-ticket-reply-btn">
                      {replySubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 h-full flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Select a ticket to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SALayout>
  );
}
